"use client";

import React from "react";
import { Award, CheckCircle, ShieldCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const services = [{
  title: "Certification professionnelle RNCP / Certification RS",
  description: "Effet Conseil conçoit, structure et sécurise les certifications RNCP & RS auprès de France Compétences. De l'étude de faisabilité jusqu'au déploiement opérationnel, l'accompagnement se réalise à chaque étape avec méthode et pragmatisme, en co-construisant avec vos équipes. Cette approche garantit la conformité des dossiers et optimise les chances d'enregistrement.",
  link: "/RncpRsCertificationsPage",
  icon: Award,
  Text: "En savoir plus",
  headerBg: "bg-[hsl(343,80%,35%)]",
  iconColor: "text-white",
  titleColor: "text-white"
}, {
  title: "Qualité en formation professionnelle",
  description: "Effet Conseil prépare les organismes à la certification Qualiopi®, qu'il s'agisse de l'audit initial, de surveillance ou de renouvellement. L'accompagnement permet d'aborder les audits avec sérénité, en décryptant les exigences du Référentiel National Qualité.",
  link: "/ProfessionalTrainingQualityPage",
  icon: CheckCircle,
  Text: "En savoir plus",
  headerBg: "bg-[#62751D]",
  iconColor: "text-white",
  titleColor: "text-white"
}, {
  title: "Cadre juridique de la formation professionnelle",
  description: "Effet Conseil aide les acteurs de la formation à naviguer dans la complexité réglementaire : conformité légale, veille règlementaire, accompagnement à la déclaration d'activité. L'objectif est de traduire les obligations légales en pratiques claires et opérationnelles. Votre sécurité juridique est une priorité.",
  link: "/TrainingLegalFrameworkPage",
  icon: ShieldCheck,
  Text: "En savoir plus",
  headerBg: "bg-[#F2D4DA]",
  iconColor: "text-[hsl(343,80%,35%)]",
  titleColor: "text-[hsl(343,80%,35%)]"
}];
export default function WhatIDoForYou() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="ce-que-je-fais-pour-vous" className="bg-background relative overflow-hidden -mb-20 ![padding:128px_0px_96px_0px]">
      {/* Fond décoratif */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-primary/5 blur-[120px]" />
        <div className="absolute top-[60%] -left-[10%] w-[40%] h-[40%] rounded-full bg-accent/20 blur-[100px]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Présentation */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
          <div className="order-2 lg:order-1">
            <h2 className="font-serif text-4xl mb-6 md:text-5xl text-[hsl(343,80%,35%)] leading-tight">
              Une expertise reconnue au service de vos ambitions
            </h2>
            <p className="text-lg mb-8 leading-relaxed max-w-xl !text-[hsl(210,10%,23%)]">Je suis Christelle CHARRIER-ROBELIN, fondatrice d’Effet Conseil. Depuis plus de 20 ans dans l'univers de la formation professionnelle, j'ai développé une expertise sur trois domaines stratégiques qui structurent et sécurisent votre activité, dans une démarche de co-construction et de responsabilité partagée.</p>
            <div className="w-24 h-1 bg-secondary rounded-full" />
          </div>

          <div className="order-1 lg:order-2 flex justify-center lg:justify-end">
            <div className="relative group max-w-md w-full">
              <div className="absolute -inset-4 bg-secondary/10 rounded-xl blur-2xl group-hover:bg-secondary/20 transition-all duration-700" />
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1606874/img/image_us1xfcee.jpg" alt="Christelle Charrier-Robelin" className="relative rounded-xl shadow-2xl w-full h-auto object-cover aspect-[4/5] border border-white/20" />
            </div>
          </div>
        </div>

        {/* Cartes services */}
        <div ref={ref} className={`grid grid-cols-1 md:grid-cols-3 gap-8 pb-8 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {services.map((service, i) => <div data-index={i} key={i} className="h-full">
              <Card className="text-card-foreground flex flex-col h-full border-none shadow-lg transition-all duration-300 hover:shadow-xl hover:-translate-y-1 rounded-xl overflow-hidden !p-0 bg-white">
                {/* Header coloré */}
                <CardHeader className={`${service.headerBg} flex flex-col items-center justify-center gap-3 py-8 px-6`}>
                  <service.icon className={`${service.iconColor} w-10 h-10 stroke-[1.5]`} />
                  <CardTitle className={`${service.titleColor} font-semibold font-serif text-xl text-center leading-tight`}>
                    {service.title}
                  </CardTitle>
                </CardHeader>

                {/* Contenu */}
                <CardContent className="flex flex-col flex-grow px-8 py-6">
                  <p className="mb-8 leading-relaxed !text-[hsl(210,10%,23%)]">
                    {service.description}
                  </p>

                  <div className="mt-auto">
                    <Button asChild variant="outline" className="w-full sm:w-auto border-[hsl(343,80%,35%)] text-[hsl(343,80%,35%)] hover:bg-[hsl(343,80%,35%)] hover:text-white transition-all duration-300 font-semibold">
                      <Link to={service.link}>{service.Text}</Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>)}
        </div>
      </div>
    </section>;
}